import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import { useNavigate } from 'react-router-dom'
import { fetchAllJobs, normalizar } from '../api/jobs'
import { fetchAllPeople, PEOPLE_QUERY_KEY, PERSON_TOPICS_PERMISSION } from '../api/people'
import { MENU_PERMISSIONS } from '../api/session'
import { fetchAllUnits, UNITS_QUERY_KEY } from '../api/units'
import { VigaIcon } from './VigaIcon'
import { VIGA } from './viga'
import { t } from '../i18n'

/**
 * s67 / ADR-0033 2i (R2, padrao Rippling; Marcelo 25/10/2026 14:16 "R2 sim" e 14:46 "J1 sim ; J2 sim") -- BUSCA NO TOPO.
 *
 * Uma caixa so na barra de cima: "Buscar cargo, pessoa ou area...". O resultado NAVEGA:
 *  - cargo -> a ficha (/cargos/:id); area -> /unidades (a tela de unidades nao tem rota por area);
 *  - pessoa -> a VIGA, com "Me conte sobre <nome>" (a pergunta segue o caminho de sempre: mascarada antes da IA);
 *  - a ULTIMA opcao e "Perguntar a VIGA: '<texto>'" (so com a IA ligada -- J2).
 * Enter sem escolher nada: com a IA ligada vai para a VIGA (J2); sem IA nao acontece nada (nao ha VIGA sem IA, D9).
 *
 * GUARDA J1: PESSOAS so para quem PODE VER NOME no assistente (`assistant.person_topics`) e com a IA ligada (o unico
 * destino de uma pessoa e a VIGA). Sem isso, `/api/employees` NEM E CHAMADO. A leitura so comeca quando a pessoa ENTRA
 * na caixa (nada e carregado so por abrir uma tela). Cargos e areas pedem a permissao do proprio menu.
 * A busca e feita AQUI, na tela, sobre o que ja foi lido: o texto digitado nao vai para lugar nenhum enquanto nao virar
 * pergunta para a VIGA.
 */

export type SearchOption =
  | { tipo: 'cargo'; id: string; label: string; detalhe: string | null }
  | { tipo: 'area'; id: string; label: string; detalhe: string | null }
  | { tipo: 'pessoa'; id: string; label: string; detalhe: string | null }
  | { tipo: 'viga'; id: 'viga'; label: string; detalhe: null }

const POR_GRUPO = 5

const grupo = (o: SearchOption): string =>
  o.tipo === 'cargo' ? t.shell.searchJobs : o.tipo === 'area' ? t.shell.searchUnits : o.tipo === 'pessoa' ? t.shell.searchPeople : ''

export function TopSearch({
  permissions,
  iaLigada,
  onAskViga,
}: {
  permissions: readonly string[]
  iaLigada: boolean
  onAskViga: (texto: string) => void
}) {
  const navigate = useNavigate()
  const [texto, setTexto] = useState('')
  const [ativo, setAtivo] = useState(false)
  const podeCargos = permissions.includes(MENU_PERMISSIONS.jobs)
  const podeAreas = permissions.includes(MENU_PERMISSIONS.units)
  // J1: sem a permissao de ver NOME no assistente (ou sem IA), a lista de pessoas nem e lida
  const podePessoas = iaLigada && podeCargos && permissions.includes(PERSON_TOPICS_PERMISSION)
  const podeViga = iaLigada && podeCargos

  const cargos = useQuery({ queryKey: ['jobs', 'all'], queryFn: fetchAllJobs, enabled: ativo && podeCargos })
  const areas = useQuery({ queryKey: UNITS_QUERY_KEY, queryFn: fetchAllUnits, enabled: ativo && podeAreas })
  const pessoas = useQuery({ queryKey: PEOPLE_QUERY_KEY, queryFn: fetchAllPeople, enabled: ativo && podePessoas })

  if (!podeCargos && !podeAreas) return null

  const alvo = normalizar(texto)
  const casa = (nome: string) => alvo.length > 0 && normalizar(nome).includes(alvo)
  const opcoes: SearchOption[] = []
  if (alvo) {
    if (podeCargos) {
      ;(cargos.data ?? [])
        .filter((j) => j.status === 'active' && (casa(j.name) || casa(j.code)))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .slice(0, POR_GRUPO)
        .forEach((j) => opcoes.push({ tipo: 'cargo', id: j.id, label: j.name, detalhe: null }))
    }
    if (podePessoas) {
      ;(pessoas.data ?? [])
        .filter((p) => casa(p.name))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .slice(0, POR_GRUPO)
        .forEach((p) => opcoes.push({ tipo: 'pessoa', id: p.id, label: p.name, detalhe: p.positionLabel }))
    }
    if (podeAreas) {
      ;(areas.data?.units ?? [])
        .filter((u) => u.status === 'active' && (casa(u.name) || casa(u.shortName ?? '')))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .slice(0, POR_GRUPO)
        .forEach((u) => opcoes.push({ tipo: 'area', id: u.id, label: u.name, detalhe: null }))
    }
    if (podeViga) opcoes.push({ tipo: 'viga', id: 'viga', label: texto.trim(), detalhe: null })
  }

  const limpa = () => setTexto('')
  const escolhe = (v: SearchOption | string | null) => {
    if (v === null) return
    if (typeof v === 'string') {
      // J2: Enter sem escolher -> VIGA (so com IA)
      const limpo = v.trim()
      if (limpo && podeViga) {
        onAskViga(limpo)
        limpa()
      }
      return
    }
    if (v.tipo === 'cargo') navigate(`/cargos/${v.id}`)
    else if (v.tipo === 'area') navigate('/unidades')
    else if (v.tipo === 'pessoa') onAskViga(`Me conte sobre ${v.label}`)
    else onAskViga(v.label)
    limpa()
  }

  return (
    <Autocomplete<SearchOption, false, false, true>
      freeSolo
      size="small"
      id="busca-topo"
      sx={{ flex: '1 1 320px', maxWidth: 480, minWidth: 160 }}
      options={opcoes}
      filterOptions={(x) => x}
      groupBy={grupo}
      renderGroup={(g) => (
        <li key={g.key}>
          {g.group && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pt: 1, fontWeight: 600 }}>
              {g.group}
            </Typography>
          )}
          <Box component="ul" sx={{ p: 0 }}>
            {g.children}
          </Box>
        </li>
      )}
      inputValue={texto}
      value={null}
      onInputChange={(_e, v, motivo) => {
        if (motivo !== 'reset') {
          setTexto(v.slice(0, 120))
          setAtivo(true)
        }
      }}
      onChange={(_e, v) => escolhe(v)}
      onFocus={() => setAtivo(true)}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(a, b) => a.tipo === b.tipo && a.id === b.id}
      noOptionsText={t.shell.searchEmpty}
      renderOption={(props, o) => {
        const { key, ...resto } = props as typeof props & { key: string }
        return (
          <Box component="li" key={`${o.tipo}-${o.id}-${key}`} {...resto} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {o.tipo === 'viga' ? (
              <>
                <VigaIcon src={VIGA.icone} size={18} />
                <Typography variant="body2">{`${t.shell.searchAsk} "${o.label}"`}</Typography>
              </>
            ) : (
              <Box>
                <Typography variant="body2">{o.label}</Typography>
                {o.detalhe && (
                  <Typography variant="caption" color="text.secondary">
                    {o.detalhe}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t.shell.searchLabel}
          placeholder={t.shell.searchPlaceholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchOutlined fontSize="small" color="action" sx={{ ml: 0.5 }} />,
          }}
        />
      )}
    />
  )
}
